"use client";

// ============================================================
// CLUBHUB – Wiederholung im Terminformular
// ============================================================
// Keine · taeglich · woechentlich an Tagen · monatlich (am 14. oder
// am zweiten Dienstag), alle n Tage/Wochen/Monate, Ende nie, an einem
// Datum oder nach n Terminen. Darunter steht die Regel in Worten und
// die naechsten Tage — so sieht man vor dem Speichern, ob „jeden
// Dienstag und Donnerstag bis 30.06.“ wirklich gemeint war.
//
// Gespeichert wird daraus eine RRULE (`lib/kalender/wiederholung.ts`).
// ============================================================
import { useMemo } from "react";
import { X } from "lucide-react";
import { PLAETTCHEN, PLAETTCHEN_AN, PLAETTCHEN_AUS, Schiene } from "@/components/liste";
import { FieldLabel } from "@/components/ui/field-label";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { WOCHENTAGE, WOCHENTAGE_KURZ, formatTagMitWochentag, istTag, tagVon, weekdayIndex, type DayKey } from "@/lib/kalender/tag";
import { ersteVorkommen, nterWochentag, regelBauen, regelKlartext, serieZaehlen, type Wiederholung, type WiederholungsArt } from "@/lib/kalender/wiederholung";
import { cn } from "@/lib/utils";

const ARTEN: { value: WiederholungsArt; label: string }[] = [
  { value: "keine", label: "Keine" },
  { value: "taeglich", label: "Täglich" },
  { value: "woechentlich", label: "Wöchentlich" },
  { value: "monatlich", label: "Monatlich" },
];

const ORDNUNG: Record<number, string> = { 1: "ersten", 2: "zweiten", 3: "dritten", 4: "vierten", [-1]: "letzten" };

export function WiederholungFeld({
  wert,
  onChange,
  start,
  uhr,
  exdates,
  onExdates,
  fehler,
}: {
  wert: Wiederholung;
  onChange: (w: Wiederholung) => void;
  /** Tag des Beginns. */
  start: DayKey;
  /** Uhrzeit des Beginns ("19:00") — fuer die Vorschau. */
  uhr: string;
  exdates: DayKey[];
  onExdates: (d: DayKey[]) => void;
  fehler?: Record<string, string>;
}) {
  const setze = (teil: Partial<Wiederholung>) => onChange({ ...wert, ...teil });
  const regel = istTag(start) ? regelBauen(wert, start) : null;
  const vorschau = useMemo(() => {
    if (!regel || !istTag(start)) return null;
    try {
      const beginn = `${start}T${uhr || "00:00"}:00`;
      const aus = new Set(exdates);
      return {
        satz: regelKlartext(regel, start),
        naechste: ersteVorkommen(beginn, regel, 6 + exdates.length).filter((d) => !aus.has(tagVon(d))).slice(0, 6),
        anzahl: serieZaehlen(beginn, regel),
      };
    } catch {
      return null;
    }
  }, [regel, start, uhr, exdates]);

  const einheit = wert.art === "taeglich" ? (wert.intervall === 1 ? "Tag" : "Tage") : wert.art === "woechentlich" ? (wert.intervall === 1 ? "Woche" : "Wochen") : wert.intervall === 1 ? "Monat" : "Monate";

  return (
    <div className="space-y-4">
      <div className="space-y-1.5">
        <FieldLabel>Wiederholung</FieldLabel>
        <Schiene className="w-fit">
          {ARTEN.map((a) => (
            <button
              key={a.value}
              type="button"
              aria-pressed={wert.art === a.value}
              onClick={() => setze({ art: a.value, tage: wert.tage.length ? wert.tage : [weekdayIndex(start)] })}
              className={cn(PLAETTCHEN, "text-body-sm px-3", wert.art === a.value ? cn(PLAETTCHEN_AN, "font-medium") : PLAETTCHEN_AUS)}
            >
              {a.label}
            </button>
          ))}
        </Schiene>
      </div>

      {wert.art !== "keine" && (
        <>
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex items-center gap-2">
              <span className="text-body-sm text-text-secondary">alle</span>
              <Input
                type="number"
                inputMode="numeric"
                min={1}
                max={52}
                value={wert.intervall}
                onChange={(e) => setze({ intervall: Math.max(1, Math.min(52, Number(e.target.value) || 1)) })}
                aria-label="Abstand"
                className="w-20 tabular-nums"
              />
              <span className="text-body-sm text-text-secondary">{einheit}</span>
            </div>
            {wert.art === "woechentlich" && (
              <div className="space-y-1.5">
                <Schiene>
                  {WOCHENTAGE_KURZ.map((t, i) => {
                    const an = wert.tage.includes(i);
                    return (
                      <button
                        key={t}
                        type="button"
                        aria-pressed={an}
                        aria-label={WOCHENTAGE[i]}
                        onClick={() => setze({ tage: an ? wert.tage.filter((x) => x !== i) : [...wert.tage, i] })}
                        className={cn(PLAETTCHEN, "text-body-sm w-9 justify-center", an ? cn(PLAETTCHEN_AN, "text-accent font-semibold") : PLAETTCHEN_AUS)}
                      >
                        {t}
                      </button>
                    );
                  })}
                </Schiene>
              </div>
            )}
            {wert.art === "monatlich" && istTag(start) && (
              <Select
                value={wert.monatsweise}
                onChange={(v) => setze({ monatsweise: v as Wiederholung["monatsweise"] })}
                options={[
                  { value: "tag", label: `am ${Number(start.slice(8, 10))}.` },
                  { value: "wochentag", label: `am ${ORDNUNG[nterWochentag(start)]} ${WOCHENTAGE[weekdayIndex(start)]}` },
                ]}
                wrapperClassName="w-56"
              />
            )}
          </div>
          {fehler?.tage && <p className="text-caption text-danger">{fehler.tage}</p>}

          <div className="flex flex-wrap items-end gap-3">
            <Select
              label="Endet"
              value={wert.ende}
              onChange={(v) => setze({ ende: v as Wiederholung["ende"] })}
              options={[
                { value: "nie", label: "Nie" },
                { value: "datum", label: "Am Datum" },
                { value: "anzahl", label: "Nach Anzahl" },
              ]}
              wrapperClassName="w-40"
            />
            {wert.ende === "datum" && <Input type="date" label="Letzter Tag" value={wert.bis} onChange={(e) => setze({ bis: e.target.value })} error={fehler?.bis} wrapperClassName="w-48" />}
            {wert.ende === "anzahl" && (
              <Input
                type="number"
                inputMode="numeric"
                label="Termine"
                min={2}
                max={500}
                value={wert.anzahl}
                onChange={(e) => setze({ anzahl: Number(e.target.value) || 0 })}
                error={fehler?.anzahl}
                wrapperClassName="w-32"
                className="tabular-nums"
              />
            )}
          </div>

          {vorschau && (
            <div className="bg-bg-secondary space-y-1 rounded-lg px-3 py-2.5">
              <p className="text-body-sm text-text-primary font-medium">
                {vorschau.satz.charAt(0).toUpperCase() + vorschau.satz.slice(1)}
                {uhr ? `, ${uhr} Uhr` : ""}
                {vorschau.anzahl ? <span className="text-text-tertiary font-normal tabular-nums"> · {vorschau.anzahl - exdates.length} Termine</span> : null}
              </p>
              <p className="text-caption text-text-secondary tabular-nums">
                Nächste: {vorschau.naechste.map((d) => formatTagMitWochentag(tagVon(d))).join(" · ")}
                {vorschau.naechste.length === 6 ? " …" : ""}
              </p>
            </div>
          )}

          {exdates.length > 0 && (
            <div className="space-y-1.5">
              <FieldLabel>Ausgelassen</FieldLabel>
              <div className="flex flex-wrap gap-1.5">
                {exdates
                  .slice()
                  .sort()
                  .map((d) => (
                    <span key={d} className="bg-bg-tertiary text-caption text-text-secondary inline-flex items-center gap-1 rounded px-1.5 py-0.5 tabular-nums">
                      {formatTagMitWochentag(d)}
                      <button type="button" onClick={() => onExdates(exdates.filter((x) => x !== d))} aria-label={`${formatTagMitWochentag(d)} wieder aufnehmen`} className="text-text-tertiary hover:text-text-primary">
                        <X className="h-3 w-3" aria-hidden />
                      </button>
                    </span>
                  ))}
              </div>
              <p className="text-caption text-text-tertiary">Ein einzelnes Vorkommen absagen oder verschieben geht im Termin selbst — dann bleibt es sichtbar.</p>
            </div>
          )}
        </>
      )}
    </div>
  );
}
